import { ReactNode } from 'react';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import { SelectSitelife } from 'src/common/components/SelectSitelife';
import {
  ProjectArchiveStatus,
  ProjectFilter,
} from 'src/project/types/projects';
import { getProjectArchiveStatusLabel, getProjectFilterLabel } from '../utils';

type Props = {
  selectedTypeFilters: ProjectFilter[];
  selectedStatusFilters: ProjectArchiveStatus[];
  onOpenFiltersModal: () => void;
};

export const MobileFilterSummary = ({
  selectedTypeFilters,
  selectedStatusFilters,
  onOpenFiltersModal,
}: Props) => {
  const { t } = useTranslation(['common', 'project']);
  const allLabel = t('common:all');

  return (
    <div className="grid w-full grid-cols-2 gap-2 min-[481px]:hidden">
      <ReadOnlyFilterField
        label={t('common:filterByType')}
        ariaLabel={t('common:filterByType')}
        onOpenFiltersModal={onOpenFiltersModal}
      >
        <ReadOnlyFilterValue
          isEmpty={selectedTypeFilters.length === 0}
          emptyLabel={allLabel}
          value={selectedTypeFilters.map((filter) => ({
            label: getProjectFilterLabel(filter, t),
            value: filter,
          }))}
        />
      </ReadOnlyFilterField>
      <ReadOnlyFilterField
        label={t('common:filterByStatus')}
        ariaLabel={t('common:filterByStatus')}
        onOpenFiltersModal={onOpenFiltersModal}
      >
        <ReadOnlyFilterValue
          isEmpty={selectedStatusFilters.length === 0}
          emptyLabel={allLabel}
          value={selectedStatusFilters.map((filter) => ({
            label: getProjectArchiveStatusLabel(filter, t),
            value: filter,
          }))}
        />
      </ReadOnlyFilterField>
    </div>
  );
};

function ReadOnlyFilterValue({
  isEmpty,
  emptyLabel,
  value,
}: {
  isEmpty: boolean;
  emptyLabel: string;
  value: { label: string; value: string }[];
}) {
  if (isEmpty) {
    return <EmptyFilterValue label={emptyLabel} />;
  }

  return (
    <SelectSitelife
      className="mt-1 w-full"
      options={[]}
      value={value}
      onChange={() => undefined}
      isMulti
      isDisabled
    />
  );
}

function EmptyFilterValue({ label }: { label: string }) {
  return (
    <div className="mt-1 flex min-h-[40px] w-full items-center justify-between rounded-sm border border-gray-300 bg-gray-100 px-1.5 text-shuttleGray-800">
      <span className="truncate">{label}</span>
      <FontAwesomeIcon
        icon={faChevronDown}
        className="ml-1 shrink-0 text-gray-600"
        aria-hidden
      />
    </div>
  );
}

function ReadOnlyFilterField({
  label,
  ariaLabel,
  onOpenFiltersModal,
  children,
}: {
  label: string;
  ariaLabel: string;
  onOpenFiltersModal: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="flex w-full cursor-pointer flex-col text-left"
      onClick={onOpenFiltersModal}
      aria-label={ariaLabel}
    >
      <span>{label}</span>
      <div className="pointer-events-none w-full">{children}</div>
    </button>
  );
}
